// familytree/src/components/TreeCanvas.jsx
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { PersonCard } from './PersonCard';
import { computeLayout, computeLines, CARD_W, CARD_H } from '../utils/treeLayout';
import { computeColorSystem } from '../utils/colorSystem';
import './TreeCanvas.css';

// How much of the tree must remain visible on each axis (in px, in screen space)
const MARGIN = 120;

export function TreeCanvas({ persons, relationships, selectedId, onSelectPerson, onEditPerson, onQuickAdd, customColors = {}, onColorChange }) {
  const containerRef = useRef();
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);

  const positions   = useMemo(() => computeLayout(persons, relationships), [persons, relationships]);
  const lines       = useMemo(() => computeLines(relationships, positions), [relationships, positions]);
  const colorSystem = useMemo(() => computeColorSystem(persons, relationships, customColors || {}), [persons, relationships, customColors]);
  // Anzahl Elternteile pro Person (für "+ Elternteil" – max. 2)
  const parentCount = useMemo(() => {
    const m = new Map();
    relationships.forEach(r => {
      if (r.type !== 'parent-child') return;
      if (!m.has(r.to)) m.set(r.to, new Set());
      m.get(r.to).add(r.from);
    });
    return m;
  }, [relationships]);

  // ── bounding box of the whole tree (in tree-space) ──────────────────
  const treeBounds = useMemo(() => {
    const posArr = [...positions.values()].filter(Boolean);
    if (posArr.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0, w: 0, h: 0 };
    const minX = Math.min(...posArr.map(p => p.x));
    const maxX = Math.max(...posArr.map(p => p.x)) + CARD_W;
    const minY = Math.min(...posArr.map(p => p.y));
    const maxY = Math.max(...posArr.map(p => p.y)) + CARD_H;
    return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY };
  }, [positions]);

  // ── clamp translation so the tree never fully leaves the viewport ────
  // x: left edge of tree (in screen px) must be > -treeW*scale + MARGIN
  //    right edge must be < containerW + treeW*scale - MARGIN  (very loose)
  // In practice we just ensure at least MARGIN px of tree is visible.
  const clamp = useCallback((x, y, scale) => {
    if (!containerRef.current) return { x, y };
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const { minX, minY, w, h } = treeBounds;
    if (w === 0 || h === 0) return { x, y };

    const treeScreenW = w * scale;
    const treeScreenH = h * scale;

    const minTX = MARGIN - (treeScreenW + minX * scale);
    const maxTX = cw - MARGIN - minX * scale;
    const cx = Math.min(maxTX, Math.max(minTX, x));

    const minTY = MARGIN - (treeScreenH + minY * scale);
    const maxTY = ch - MARGIN - minY * scale;
    const cy = Math.min(maxTY, Math.max(minTY, y));

    return { x: isFinite(cx) ? cx : x, y: isFinite(cy) ? cy : y };
  }, [treeBounds]);

  // ── center on mount / when persons count changes ─────────────────────
  useEffect(() => {
    if (persons.length === 0 || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const { minX, minY, w, h } = treeBounds;
    if (w === 0 && h === 0) return;

    const scale = Math.min(1, Math.min((cw - 80) / Math.max(w, 1), (ch - 120) / Math.max(h, 1)));
    const x = (cw - w * scale) / 2 - minX * scale;
    const y = (ch - h * scale) / 2 - minY * scale;
    setTransform({ x, y, scale });
  }, [persons.length]); // eslint-disable-line

  // ── wheel zoom ───────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    setTransform(t => {
      const newScale = Math.min(3, Math.max(0.15, t.scale * delta));
      const sc = newScale / t.scale;
      const rawX = mx - (mx - t.x) * sc;
      const rawY = my - (my - t.y) * sc;
      const { x, y } = clamp(rawX, rawY, newScale);
      return { x, y, scale: newScale };
    });
  }, [clamp]);

  // ── mouse panning ────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.person-card')) return;
    e.preventDefault();
    setIsPanning(true);
    panStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
  }, [transform]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning || !panStart.current) return;
    const start = panStart.current;
    if (!start || start.x == null) return;
    setTransform(t => {
      const rawX = e.clientX - start.x;
      const rawY = e.clientY - start.y;
      const { x, y } = clamp(rawX, rawY, t.scale);
      return { ...t, x, y };
    });
  }, [isPanning, clamp]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    panStart.current = null;
  }, []);

  // ── Touch (Handy/Tablet): 1 Finger = verschieben, 2 Finger = zoomen ──
  // Wichtig: Alle Werte werden VOR setTransform berechnet. Früher wurde im
  // setTransform-Callback auf panStart.current zugegriffen – war der Finger
  // inzwischen losgelassen (= null), stürzte die App ab (weisser Bildschirm).
  const transformRef = useRef(transform);
  useEffect(() => { transformRef.current = transform; }, [transform]);
  const touchRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const mid = (a, b) => {
      const r = el.getBoundingClientRect();
      return { x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top };
    };
    const dist = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
    const startPan = (p, moved) => {
      const t = transformRef.current;
      touchRef.current = { mode: 'pan', sx: p.clientX - t.x, sy: p.clientY - t.y, ox: p.clientX, oy: p.clientY, moved };
    };

    const onStart = (e) => {
      if (e.touches.length === 1) {
        startPan(e.touches[0], false);
      } else if (e.touches.length === 2) {
        const [a, b] = e.touches;
        const m = mid(a, b);
        touchRef.current = { mode: 'pinch', d: dist(a, b), mx: m.x, my: m.y, start: { ...transformRef.current } };
      }
    };

    const onMove = (e) => {
      const s = touchRef.current;
      if (!s) return;
      if (s.mode === 'pan' && e.touches.length === 1) {
        const p = e.touches[0];
        // kleine Bewegungen gelten noch als Antippen (damit Klicks auf Karten funktionieren)
        if (!s.moved && Math.hypot(p.clientX - s.ox, p.clientY - s.oy) < 8) return;
        s.moved = true;
        e.preventDefault();
        const rawX = p.clientX - s.sx;
        const rawY = p.clientY - s.sy;
        const scale = transformRef.current.scale;
        const { x, y } = clamp(rawX, rawY, scale);
        const next = { x, y, scale };
        transformRef.current = next;
        setTransform(next);
      } else if (s.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        const m = mid(a, b);
        const st = s.start;
        const scale = Math.min(3, Math.max(0.15, st.scale * dist(a, b) / s.d));
        // Punkt unter der ursprünglichen Fingermitte bleibt unter den Fingern
        const tx = (s.mx - st.x) / st.scale;
        const ty = (s.my - st.y) / st.scale;
        const { x, y } = clamp(m.x - tx * scale, m.y - ty * scale, scale);
        const next = { x, y, scale };
        transformRef.current = next;
        setTransform(next);
      }
    };

    const onEnd = (e) => {
      if (e.touches.length === 1) startPan(e.touches[0], true); // von 2 auf 1 Finger
      else if (e.touches.length === 0) touchRef.current = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [clamp]);

  // ── attach wheel listener ────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── zoom buttons ─────────────────────────────────────────────────────
  const zoom = useCallback((delta) => {
    if (!containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    setTransform(t => {
      const newScale = Math.min(3, Math.max(0.15, t.scale * delta));
      const sc = newScale / t.scale;
      const rawX = cw / 2 - (cw / 2 - t.x) * sc;
      const rawY = ch / 2 - (ch / 2 - t.y) * sc;
      const { x, y } = clamp(rawX, rawY, newScale);
      return { x, y, scale: newScale };
    });
  }, [clamp]);

  // ── recenter button ──────────────────────────────────────────────────
  const recenter = useCallback(() => {
    if (persons.length === 0 || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const { minX, minY, w, h } = treeBounds;
    if (w === 0 && h === 0) return;
    const scale = Math.min(1, Math.min((cw - 80) / Math.max(w, 1), (ch - 120) / Math.max(h, 1)));
    const x = (cw - w * scale) / 2 - minX * scale;
    const y = (ch - h * scale) / 2 - minY * scale;
    setTransform({ x, y, scale });
  }, [persons.length, treeBounds]); // eslint-disable-line

  // ── render ───────────────────────────────────────────────────────────
  const cssTransform = `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`;

  return (
    <div
      ref={containerRef}
      className={`tree-canvas ${isPanning ? 'tree-canvas--panning' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {persons.length === 0 && (
        <div className="canvas-empty">
          <div className="canvas-empty-icon"></div>
          <h2>Dein Stammbaum ist leer</h2>
          <p>Klicke auf <strong>+ Person hinzufügen</strong> um zu beginnen.</p>
        </div>
      )}

      {/* SVG connection lines – uses overflow:visible, no fixed size needed */}
      <svg
        className="tree-lines"
        style={{ transform: cssTransform, transformOrigin: '0 0' }}
      >
        {lines.map((line, i) => {
          const isPartner    = line.type === 'partner' || line.type === 'ex-partner';
          const isExPartner  = line.type === 'ex-partner';
          const stroke       = isPartner ? (isExPartner ? '#BBBBBB' : '#6BA3D6') : '#AAAAAA';
          const strokeWidth  = isPartner ? '2' : '1.5';
          const dashArray    = line.dash || (isExPartner ? '6,4' : undefined);
          return (
            <path
              key={i}
              d={line.path}
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
      </svg>

      {/* Person cards */}
      <div
        className="tree-cards"
        style={{ transform: cssTransform, transformOrigin: '0 0' }}
      >
        {persons.map(person => {
          const pos = positions.get(person.id);
          if (!pos) return null;
          return (
            <PersonCard
              key={person.id}
              person={person}
              selected={person.id === selectedId}
              onClick={onSelectPerson}
              onEdit={onEditPerson}
              onQuickAdd={onQuickAdd}
              colorInfo={colorSystem.get(person.id)}
              onColorChange={onColorChange}
              canAddParent={(parentCount.get(person.id)?.size || 0) < 2}
              style={{ left: pos.x, top: pos.y }}
            />
          );
        })}
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls" data-tour="zoom">
        <button onClick={() => zoom(1.2)} title="Reinzoomen" className="zoom-btn">+</button>
        <button onClick={recenter} title="Zentrieren" className="zoom-btn zoom-btn--center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3l7 7M21 3l-7 7M3 21l7-7M21 21l-7-7"/>
          </svg>
        </button>
        <button onClick={() => zoom(0.83)} title="Rauszoomen" className="zoom-btn">−</button>
      </div>

      <div className="scale-indicator">{Math.round(transform.scale * 100)}%</div>
    </div>
  );
}
